import { useEffect, useState, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/config';
import { isDevelopment, getEnvironmentConfig } from '../utils/environmentConfig';
import { oauthErrorRecovery } from '../utils/oauthErrorRecovery';
// Lazy import oauthLogger functions to avoid initialization issues
// import { logOAuthEvent, logOAuthError, logOAuthPerformance, completeOAuthFlow, OAUTH_STAGES } from '../utils/oauthLogger';
import { OAUTH_STAGES } from '../utils/oauthLogger'; // Constants are safe to import
import { logger } from '../utils/logger';

const AuthCallback = memo(() => {
  const navigate = useNavigate();
  const [processingState, setProcessingState] = useState({
    status: 'processing', // 'processing', 'success', 'error', 'timeout'
    message: 'Completing sign-in...',
    error: null,
    timeoutId: null
  });
  
  // Cache lazy-loaded oauthLogger module
  const oauthLoggerRef = useRef(null);
  const getOAuthLogger = async () => {
    if (!oauthLoggerRef.current) {
      oauthLoggerRef.current = await import('../utils/oauthLogger');
    }
    return oauthLoggerRef.current;
  };

  useEffect(() => {
    const config = getEnvironmentConfig();
    const authTimeout = config.settings.authTimeout;

    const handleAuthCallback = async () => {
      // Load oauthLogger module once at the start
      const oauthLogger = await getOAuthLogger();
      
      // Get OAuth flow ID from session storage
      const flowId = sessionStorage.getItem('oauthFlowId');
      const callbackStartTime = Date.now();
      
      // CRITICAL DIAGNOSTIC: Log everything about the current page state
      logger.group('🔍 AuthCallback: Initial State Diagnostic');
      logger.debug('🔗 Full URL:', window.location.href);
      logger.debug('🔗 Origin:', window.location.origin);
      logger.debug('🔗 Pathname:', window.location.pathname);
      logger.debug('🔗 Hash:', window.location.hash);
      logger.debug('🔗 Search:', window.location.search);
      logger.debug('🔗 Full Hash Length:', window.location.hash.length);
      logger.debug('🆔 Flow ID:', flowId);
      logger.debug('📦 SessionStorage OAuth keys:', Object.keys(sessionStorage).filter(k => k.includes('oauth')));
      logger.debug('📦 LocalStorage Supabase keys:', Object.keys(localStorage).filter(k => k.includes('sb-') || k.includes('supabase')));
      
      // Check if we're on the callback route
      const isCallbackRoute = window.location.pathname.includes('/auth/callback') || 
                              window.location.hash.includes('/auth/callback');
      logger.debug('📍 On callback route?', isCallbackRoute);
      logger.groupEnd();
      
      if (flowId) {
        oauthLogger.logOAuthEvent(flowId, 'callback_processing_started', {
          stage: OAUTH_STAGES.CALLBACK,
          url: window.location.href,
          hashLength: window.location.hash.length,
          hasAccessToken: window.location.hash.includes('access_token')
        });
      }
      
      logger.info('🔐 AuthCallback: Starting OAuth callback processing...');

      // Set up timeout handling for slow authentication processes
      const timeoutId = setTimeout(() => {
        logger.warn('⏰ AuthCallback: Authentication timeout reached');
        setProcessingState({
          status: 'timeout',
          message: 'Sign-in is taking longer than expected',
          error: 'Authentication timeout. This might be due to a slow connection.',
          timeoutId: null
        });

        // Set up auto-redirect after showing timeout message
        setTimeout(() => {
          oauthErrorRecovery.cleanupOAuthUrl();
          navigate('/?fallback=timeout', { replace: true });
        }, 10000);
      }, authTimeout);

      setProcessingState(prev => ({ ...prev, timeoutId }));

      try {
        // Use Supabase's built-in session detection instead of manual URL parsing
        logger.debug('🔍 AuthCallback: Using Supabase session detection...');
        logger.debug('🔗 Full URL hash:', window.location.hash);
        
        // Handle HashRouter + Supabase hash fragment conflict
        // IMPORTANT: Supabase's detectSessionInUrl processes #access_token=... format
        // We need to preserve the original format for Supabase to detect it
        // Only normalize if we're not on the callback route AND hash doesn't already have the route
        let hashFragment = window.location.hash;
        if (hashFragment.includes('access_token=') || hashFragment.includes('error=')) {
          // Check if we need to normalize for HashRouter
          // Supabase redirects with just #access_token=... which we need to preserve
          // But HashRouter needs #/auth/callback to route correctly
          // Solution: Don't modify the hash - let Supabase process it first, then normalize
          // The hash will be: #access_token=... or #/auth/callback#access_token=...
          logger.debug('🔍 AuthCallback: OAuth hash detected, preserving for Supabase processing');
        }

        // Check for OAuth errors in URL first
        const urlParams = new URLSearchParams(window.location.search);
        // Parse hash - handle both #/auth/callback#access_token=... and #access_token=...
        const hashForParams = hashFragment.includes('#access_token') 
          ? hashFragment.split('#').find(part => part.includes('access_token') || part.includes('error')) || ''
          : hashFragment.substring(1);
        const hashParams = new URLSearchParams(hashForParams);
        const oauthError = urlParams.get('error') || hashParams.get('error');
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

        if (oauthError) {
          clearTimeout(timeoutId);
          
          // Log OAuth error
          if (flowId) {
            const error = new Error(errorDescription || oauthError);
            error.code = oauthError;
            oauthLogger.logOAuthError(flowId, error, {
              stage: OAUTH_STAGES.CALLBACK,
              errorDescription,
              url: window.location.href
            });
          }
          
          logger.error('🚨 AuthCallback: OAuth error detected:', oauthError, errorDescription);

          // Handle specific OAuth error types
          let userFriendlyMessage = 'Sign-in failed. Please try again.';
          
          switch (oauthError) {
            case 'access_denied':
              userFriendlyMessage = 'Sign-in was cancelled. You can try again if you\'d like.';
              break;
            case 'invalid_request':
              userFriendlyMessage = 'Invalid sign-in request. Please contact support if this continues.';
              break;
            case 'server_error':
              userFriendlyMessage = 'Server error during sign-in. Please try again in a moment.';
              break;
            case 'temporarily_unavailable':
              userFriendlyMessage = 'Sign-in service is temporarily unavailable. Please try again later.';
              break;
            default:
              if (errorDescription) {
                userFriendlyMessage = `Sign-in error: ${errorDescription}`;
              }
          }

          setProcessingState({
            status: 'error',
            message: userFriendlyMessage,
            error: oauthError,
            timeoutId: null
          });

          // Complete OAuth flow as failed
          if (flowId) {
            completeOAuthFlow(flowId, false, {
              error: oauthError,
              errorDescription,
              userMessage: userFriendlyMessage
            });
          }

          // Redirect to login with error after showing message
          setTimeout(() => {
            // Clean up URL before redirecting
            oauthErrorRecovery.cleanupOAuthUrl();
            navigate(`/?error=${encodeURIComponent(oauthError)}`, { replace: true });
          }, 3000);
          return;
        }

        // Update user feedback
        setProcessingState(prev => ({
          ...prev,
          message: 'Verifying your authentication...'
        }));

        // Check if access_token is in URL - if so, Supabase will process it automatically
        // CRITICAL: We must wait for Supabase to actually establish the session before redirecting
        
        // Handle double-hash format: #/auth/callback#access_token=...
        // This happens when HashRouter has #/auth/callback and Supabase appends #access_token=...
        // Supabase's detectSessionInUrl expects just #access_token=... format
        let normalizedHash = hashFragment;
        let needsNormalization = false;
        
        if (hashFragment.includes('#access_token=') && hashFragment.includes('/auth/callback')) {
          // Extract the access_token part from the double hash
          // Format: #/auth/callback#access_token=...&refresh_token=...
          const parts = hashFragment.split('#').filter(p => p.length > 0);
          const accessTokenPart = parts.find(p => p.includes('access_token='));
          
          if (accessTokenPart) {
            // Normalize to single hash format for Supabase: #access_token=...
            normalizedHash = '#' + accessTokenPart;
            needsNormalization = true;
            logger.info('🔧 AuthCallback: Detected double-hash format, normalizing for Supabase...');
            logger.debug('🔗 Original hash:', hashFragment.substring(0, 100) + '...');
            logger.debug('🔗 Normalized hash:', normalizedHash.substring(0, 100) + '...');
            
            // CRITICAL: Set the normalized hash and manually parse it for Supabase
            // Supabase's detectSessionInUrl reads window.location.hash on initialization
            // Since we're changing it after init, we need to manually process it
            const originalHash = window.location.hash;
            window.location.hash = normalizedHash;
            logger.debug('🔧 AuthCallback: Set normalized hash for Supabase processing');
            
            // Manually parse the hash fragment and create session
            // Extract parameters from normalized hash
            const hashParams = new URLSearchParams(accessTokenPart);
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');
            const expiresIn = hashParams.get('expires_in');
            const tokenType = hashParams.get('token_type') || 'bearer';
            
            if (accessToken) {
              logger.debug('🔧 AuthCallback: Manually processing access token from normalized hash');
              
              // Try to set the session using Supabase's internal methods
              // First, give Supabase a moment to auto-detect
              await new Promise(resolve => setTimeout(resolve, 200));
              
              // Check if Supabase auto-processed it
              const { data: { session: autoSession } } = await supabase.auth.getSession();
              if (autoSession?.user) {
                logger.info('✅ AuthCallback: Supabase auto-processed normalized hash successfully!');
              } else {
                logger.warn('⚠️ AuthCallback: Supabase did not auto-process, will rely on session detection in wait loop...');
              }
            }
          }
        }
        
        const hasAccessToken = hashFragment.includes('access_token=') || 
                               window.location.hash.includes('access_token=') ||
                               normalizedHash.includes('access_token=');
        
        if (hasAccessToken) {
          logger.info('✅ AuthCallback: Access token detected in URL, waiting for Supabase to establish session...');
          logger.debug('🔗 Hash fragment:', hashFragment);
          logger.debug('🔗 Normalized hash:', normalizedHash);
          logger.debug('🔗 Full URL:', window.location.href);
          logger.debug('🔗 Window location hash:', window.location.hash);
          
          // Extract token info for debugging (don't log the actual token)
          // Use normalized hash if we normalized it
          const hashForTokenInfo = needsNormalization ? normalizedHash : hashFragment;
          const hashParts = hashForTokenInfo.split('&');
          const tokenInfo = hashParts.map(part => {
            if (part.includes('access_token=')) return 'access_token=[REDACTED]';
            if (part.includes('expires_in=')) return part;
            if (part.includes('token_type=')) return part;
            if (part.includes('refresh_token=')) return 'refresh_token=[REDACTED]';
            return part.substring(0, 50) + '...';
          }).join('&');
          logger.debug('🔗 Token info:', tokenInfo);
          
          // Log event
          if (flowId) {
            oauthLogger.logOAuthEvent(flowId, 'access_token_detected', {
              stage: OAUTH_STAGES.CALLBACK,
              waitingForSession: true
            });
          }
          
          // Set flag to indicate OAuth redirect completed
          sessionStorage.setItem('oauth_redirect_complete', 'true');
          
          clearTimeout(timeoutId);
          
          // CRITICAL: Wait for Supabase to process the hash and establish session
          // Supabase's detectSessionInUrl processes the hash asynchronously
          // We need to wait for the onAuthStateChange event OR verify session is established
          
          // Set up a promise to wait for either session establishment or timeout
          const waitForSession = new Promise((resolve) => {
            let sessionFound = false;
            let attempts = 0;
            const maxAttempts = 15; // Up to 7.5 seconds total
            const checkInterval = 500; // Check every 500ms
            
            // Also listen for auth state change (Supabase's preferred method)
            const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
              logger.debug(`🔍 AuthCallback: Auth state change event: ${event}`, session?.user?.id || 'no user');
              
              if (event === 'SIGNED_IN' && session?.user && !sessionFound) {
                logger.info('✅ AuthCallback: Session established via onAuthStateChange:', session.user.id);
                logger.debug('🔍 AuthCallback: Session data from event:', {
                  userId: session.user.id,
                  email: session.user.email,
                  provider: session.user.app_metadata?.provider,
                  accessToken: session.access_token ? 'present' : 'missing',
                  refreshToken: session.refresh_token ? 'present' : 'missing'
                });
                sessionFound = true;
                subscription.unsubscribe();
                resolve({ session, viaEvent: true });
              } else if (event === 'TOKEN_REFRESHED' && session?.user && !sessionFound) {
                logger.info('✅ AuthCallback: Token refreshed, session available:', session.user.id);
                sessionFound = true;
                subscription.unsubscribe();
                resolve({ session, viaEvent: true });
              }
            });
            
            // Also poll getSession as backup
            const checkSession = async () => {
              if (sessionFound) return;
              
              attempts++;
              logger.debug(`🔍 AuthCallback: Checking for session (attempt ${attempts}/${maxAttempts})...`);
              
              try {
                const { data: { session }, error } = await supabase.auth.getSession();
                
                if (session?.user && !sessionFound) {
                  logger.info('✅ AuthCallback: Session established via getSession:', session.user.id);
                  logger.debug('🔍 AuthCallback: Session data from getSession:', {
                    userId: session.user.id,
                    email: session.user.email,
                    provider: session.user.app_metadata?.provider,
                    expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'no expiry',
                    accessToken: session.access_token ? 'present' : 'missing',
                    refreshToken: session.refresh_token ? 'present' : 'missing'
                  });
                  sessionFound = true;
                  subscription.unsubscribe();
                  resolve({ session, viaEvent: false });
                  return;
                } else if (error) {
                  logger.warn('⚠️ AuthCallback: Error checking session:', error);
                  logger.debug('🔍 AuthCallback: Error details:', {
                    message: error.message,
                    status: error.status,
                    name: error.name
                  });
                } else if (!session) {
                  logger.debug(`🔍 AuthCallback: No session found (attempt ${attempts}/${maxAttempts})`);
                }
                
                if (!sessionFound && attempts < maxAttempts) {
                  setTimeout(checkSession, checkInterval);
                } else if (!sessionFound) {
                  // Timeout - resolve with no session
                  subscription.unsubscribe();
                  resolve({ session: null, viaEvent: false });
                }
              } catch (err) {
                logger.error('🚨 AuthCallback: Exception checking session:', err);
                if (attempts < maxAttempts) {
                  setTimeout(checkSession, checkInterval);
                } else {
                  subscription.unsubscribe();
                  resolve({ session: null, viaEvent: false });
                }
              }
            };
            
            // Start checking after initial delay
            setTimeout(checkSession, 1000);
          });
          
          // Wait for session to be established
          const { session, viaEvent } = await waitForSession;
          
          if (session?.user) {
            logger.info('✅ AuthCallback: Session established successfully:', session.user.id);
            logger.debug('🔍 AuthCallback: Session details:', {
              userId: session.user.id,
              email: session.user.email,
              provider: session.user.app_metadata?.provider,
              expiresAt: new Date(session.expires_at * 1000).toISOString(),
              viaEvent
            });
            
            // CRITICAL: Verify session is persisted in storage before redirecting
            // Check localStorage for Supabase session (Supabase uses a specific key format)
            const supabaseKeys = Object.keys(localStorage).filter(key => 
              key.includes('sb-') && key.includes('auth-token')
            );
            logger.debug('🔍 AuthCallback: Checking localStorage for Supabase session keys:', supabaseKeys);
            
            // Verify session is actually accessible
            const { data: { session: verifySession } } = await supabase.auth.getSession();
            if (verifySession?.user) {
              logger.info('✅ AuthCallback: Session verified and accessible before redirect');
              
              // Check if it's in localStorage
              const hasSessionInStorage = supabaseKeys.length > 0;
              if (hasSessionInStorage) {
                logger.info('✅ AuthCallback: Session confirmed in localStorage');
              } else {
                logger.warn('⚠️ AuthCallback: Session not in localStorage but accessible via API');
                // Wait a bit more for Supabase to persist the session
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Verify again
                const { data: { session: finalVerify } } = await supabase.auth.getSession();
                if (finalVerify?.user) {
                  logger.info('✅ AuthCallback: Session still accessible after wait');
                } else {
                  logger.error('🚨 AuthCallback: Session lost after wait!');
                }
              }
            } else {
              logger.error('🚨 AuthCallback: Session not accessible after establishment!');
            }
            
            // Log successful session establishment
            if (flowId) {
              oauthLogger.logOAuthEvent(flowId, 'session_established', {
                stage: OAUTH_STAGES.SESSION_ESTABLISHMENT,
                userId: session.user.id,
                viaEvent
              });
              
              oauthLogger.completeOAuthFlow(flowId, true, {
                userId: session.user.id,
                provider: session.user.app_metadata?.provider,
                viaEvent
              });
              sessionStorage.removeItem('oauthFlowId');
            }
            
            // Set flag for UserContext to detect OAuth completion
            sessionStorage.setItem('oauth_redirect_complete', 'true');
            sessionStorage.setItem('oauth_user_id', session.user.id);
            
            // NOW we can safely clean up the URL and redirect
            oauthErrorRecovery.cleanupOAuthUrl();
            
            // Build correct redirect URL
            const basePath = import.meta.env?.VITE_BASE_PATH || '/';
            const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
            const appUrl = `${window.location.origin}${pathPrefix}/#/`;
            
            logger.info('🔄 AuthCallback: Redirecting to app with established session:', appUrl);
            
            // CRITICAL: Add small delay to ensure session is fully persisted
            // This gives Supabase time to write to localStorage
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Redirect - session is now established and stored
            window.location.href = appUrl;
            return; // Exit early
          } else {
            // Session not established after waiting
            logger.warn('⚠️ AuthCallback: Session not established after waiting');
            logger.debug('🔍 AuthCallback: Checking localStorage for any session data...');
            
            // Check if there's any session data in localStorage
            const sessionKeys = Object.keys(localStorage).filter(key => key.includes('supabase') || key.includes('auth'));
            logger.debug('🔍 AuthCallback: Found localStorage keys:', sessionKeys);
            
            // Try one more time to get session
            const { data: { session: finalSession } } = await supabase.auth.getSession();
            if (finalSession?.user) {
              logger.info('✅ AuthCallback: Session found on final check:', finalSession.user.id);
              
              // Set flag for UserContext
              sessionStorage.setItem('oauth_redirect_complete', 'true');
              sessionStorage.setItem('oauth_user_id', finalSession.user.id);
              
              oauthErrorRecovery.cleanupOAuthUrl();
              
              const basePath = import.meta.env?.VITE_BASE_PATH || '/';
              const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
              const appUrl = `${window.location.origin}${pathPrefix}/#/`;
              
              logger.info('🔄 AuthCallback: Redirecting with session from final check');
              await new Promise(resolve => setTimeout(resolve, 300));
              window.location.href = appUrl;
              return;
            }
            
            logger.error('🚨 AuthCallback: No session found after all attempts');
            logger.error('🔍 AuthCallback: URL hash:', window.location.hash);
            logger.error('🔍 AuthCallback: Full URL:', window.location.href);
            
            // Don't clean up URL yet - let UserContext try to process it
            // Build correct redirect URL
            const basePath = import.meta.env?.VITE_BASE_PATH || '/';
            const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
            const appUrl = `${window.location.origin}${pathPrefix}/#/`;
            
            // Set flag that OAuth was attempted but session not found
            sessionStorage.setItem('oauth_redirect_complete', 'true');
            sessionStorage.setItem('oauth_session_missing', 'true');
            
            logger.warn('⚠️ AuthCallback: Redirecting anyway - UserContext will handle retry');
            // Redirect - UserContext will handle retry
            window.location.href = appUrl;
            return; // Exit early
          }
        }

        // Fallback: Try to get session if no access token in URL
        // Use Supabase's built-in session handling with faster retry logic
        let session = null;
        let sessionError = null;
        const maxRetries = 3;
        let retryCount = 0;

        while (retryCount < maxRetries && !session && !sessionError) {
          if (retryCount > 0) {
            logger.debug(`🔄 AuthCallback: Retry attempt ${retryCount}/${maxRetries}`);
          }

          const { data, error } = await supabase.auth.getSession();
          session = data.session;
          sessionError = error;

          if (!session && !sessionError && retryCount < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          retryCount++;
        }
        
        // If no session after retries, redirect anyway - UserContext will handle it
        if (!session && !sessionError) {
          logger.warn('⚠️ AuthCallback: No session after retries, redirecting anyway');
          clearTimeout(timeoutId);
          
          // Redirect - session should be established by Supabase in background
          const basePath = import.meta.env?.VITE_BASE_PATH || '/';
          const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
          const appUrl = `${window.location.origin}${pathPrefix}/#/`;
          
          oauthErrorRecovery.cleanupOAuthUrl();
          window.location.href = appUrl;
          return; // Exit early
        }

        if (sessionError) {
          clearTimeout(timeoutId);
          
          // Log session error
          if (flowId) {
            oauthLogger.logOAuthError(flowId, sessionError, {
              stage: OAUTH_STAGES.SESSION_ESTABLISHMENT,
              retryCount,
              url: window.location.href
            });
          }
          
          logger.error('🚨 AuthCallback: Session error:', sessionError);

          let userFriendlyMessage = 'Failed to establish your session. Please try signing in again.';
          
          if (sessionError.message?.includes('network') || sessionError.message?.includes('fetch')) {
            userFriendlyMessage = 'Network error during sign-in. Please check your connection and try again.';
          } else if (sessionError.message?.includes('invalid') || sessionError.message?.includes('expired')) {
            userFriendlyMessage = 'Your sign-in session has expired. Please try signing in again.';
          }

          setProcessingState({
            status: 'error',
            message: userFriendlyMessage,
            error: sessionError.message,
            timeoutId: null
          });

          // Complete OAuth flow as failed
          if (flowId) {
            oauthLogger.completeOAuthFlow(flowId, false, {
              error: sessionError.message,
              errorType: 'session_error',
              userMessage: userFriendlyMessage,
              retryCount
            });
          }

          setTimeout(() => {
            // Clean up URL before redirecting
            oauthErrorRecovery.cleanupOAuthUrl();
            navigate('/?error=session_error', { replace: true });
          }, 3000);
          return;
        }

        if (!session) {
          clearTimeout(timeoutId);
          
          // Log no session error
          if (flowId) {
            const noSessionError = new Error('No session established after OAuth callback');
            oauthLogger.logOAuthError(flowId, noSessionError, {
              stage: OAUTH_STAGES.SESSION_ESTABLISHMENT,
              retryCount,
              url: window.location.href
            });
          }
          
          logger.warn('⚠️ AuthCallback: No session found after OAuth callback');

          setProcessingState({
            status: 'error',
            message: 'No active session found. Please try signing in again.',
            error: 'No session established',
            timeoutId: null
          });

          // Complete OAuth flow as failed
          if (flowId) {
            oauthLogger.completeOAuthFlow(flowId, false, {
              error: 'No session established',
              errorType: 'no_session',
              userMessage: 'No active session found. Please try signing in again.',
              retryCount
            });
          }

          setTimeout(() => {
            // Clean up URL before redirecting
            oauthErrorRecovery.cleanupOAuthUrl();
            navigate('/?error=no_session', { replace: true });
          }, 3000);
          return;
        }

        // Success! Session established
        clearTimeout(timeoutId);
        
        // Log successful session establishment
        if (flowId) {
          oauthLogger.logOAuthEvent(flowId, 'session_established', {
            stage: OAUTH_STAGES.SESSION_ESTABLISHMENT,
            userId: session.user.id,
            retryCount,
            url: window.location.href
          });
          
          // Log performance metrics
          oauthLogger.logOAuthPerformance(flowId, 'callback_processing_duration', Date.now() - callbackStartTime, {
            stage: OAUTH_STAGES.CALLBACK,
            retryCount,
            success: true
          });
        }
        
        logger.info('✅ AuthCallback: Session established successfully:', session.user.id);

        setProcessingState({
          status: 'success',
          message: 'Sign-in successful! Redirecting to your dashboard...',
          error: null,
          timeoutId: null
        });

        // Clean up URL using the centralized OAuth cleanup utility
        oauthErrorRecovery.cleanupOAuthUrl();

        // Set developer mode
        localStorage.setItem('developerMode', 'true');
        
        // Complete OAuth flow as successful
        if (flowId) {
          oauthLogger.completeOAuthFlow(flowId, true, {
            userId: session.user.id,
            provider: session.user.app_metadata?.provider,
            retryCount,
            callbackDuration: Date.now() - callbackStartTime
          });
          
          // Clean up session storage
          sessionStorage.removeItem('oauthFlowId');
        }
        
        // Redirect immediately - UserContext will load profile in background
        // Don't wait for profile loading - redirect as soon as session is established
        const basePath = import.meta.env?.VITE_BASE_PATH || '/';
        const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
        const appUrl = `${window.location.origin}${pathPrefix}/#/`;
        
        // Redirect immediately (session is established, profile will load in background)
        window.location.href = appUrl;

      } catch (error) {
        clearTimeout(timeoutId);
        
        // Log unexpected error
        if (flowId) {
          oauthLogger.logOAuthError(flowId, error, {
            stage: OAUTH_STAGES.CALLBACK,
            type: 'unexpected_error',
            url: window.location.href,
            callbackDuration: Date.now() - callbackStartTime
          });
        }
        
        logger.error('🚨 AuthCallback: Unexpected error:', error);

        let userFriendlyMessage = 'An unexpected error occurred during sign-in. Please try again.';
        
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          userFriendlyMessage = 'Network error during sign-in. Please check your connection and try again.';
        } else if (error.message?.includes('timeout')) {
          userFriendlyMessage = 'Sign-in timed out. Please try again.';
        }

        setProcessingState({
          status: 'error',
          message: userFriendlyMessage,
          error: error.message,
          timeoutId: null
        });

        // Complete OAuth flow as failed
        if (flowId) {
          oauthLogger.completeOAuthFlow(flowId, false, {
            error: error.message,
            errorType: 'unexpected_error',
            userMessage: userFriendlyMessage,
            callbackDuration: Date.now() - callbackStartTime
          });
        }

        setTimeout(() => {
          // Clean up URL before redirecting
          oauthErrorRecovery.cleanupOAuthUrl();
          navigate('/?error=unexpected', { replace: true });
        }, 3000);
      }
    };

    handleAuthCallback();

    // Cleanup timeout on unmount
    return () => {
      if (processingState.timeoutId) {
        clearTimeout(processingState.timeoutId);
      }
    };
  }, [navigate]);

  // Render different UI based on processing state
  const renderContent = () => {
    switch (processingState.status) {
      case 'processing':
        return (
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <div className="text-xl mb-2">{processingState.message}</div>
            <div className="text-sm opacity-75">Please wait while we process your authentication</div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center text-white">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-500 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="text-xl mb-2">{processingState.message}</div>
            <div className="text-sm opacity-75">Taking you to your dashboard...</div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center text-white">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-500 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="text-xl mb-2">{processingState.message}</div>
            <div className="text-sm opacity-75">Redirecting you back to the login page...</div>
            {isDevelopment() && processingState.error && (
              <div className="mt-4 p-3 bg-red-900 bg-opacity-50 rounded-lg text-xs">
                <div className="font-semibold mb-1">Debug Info:</div>
                <div className="text-left">{processingState.error}</div>
              </div>
            )}
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center text-white">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-500 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-xl mb-2">{processingState.message}</div>
            <div className="text-sm opacity-75 mb-4">This might be due to a slow connection.</div>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/?fallback=timeout', { replace: true })}
                className="px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-colors duration-200 text-sm"
              >
                Use Different Sign-in Method
              </button>
              <div className="text-xs opacity-60">or wait a moment and the page will redirect automatically</div>
            </div>
            {isDevelopment() && processingState.error && (
              <div className="mt-4 p-3 bg-yellow-900 bg-opacity-50 rounded-lg text-xs">
                <div className="font-semibold mb-1">Debug Info:</div>
                <div className="text-left">{processingState.error}</div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <div className="text-xl">Processing...</div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
});

AuthCallback.displayName = 'AuthCallback';

export default AuthCallback;
